import React from 'react';
import { AnalyticsPoint } from '../../../../shared/admin.analytics';
import { LangBlock } from '../../global';

export const CHART_COLORS = [
    '#7cb5ff',
    '#95ff63',
    '#22d3ee',
    '#c084fc',
    '#f3d139',
    '#fb7185',
    '#34d399',
    '#f97316',
];

export type ChartSeries = {
    key: string;
    label: string;
    color: string;
    points: AnalyticsPoint[];
};

type LineChartProps = {
    series: ChartSeries[];
    height?: number;
    formatX?: (t: number) => string;
    formatY?: (v: number) => string;
    emptyMessage?: string;
};

const VIEW_W = 800;
const VIEW_H = 280;
const PAD = { l: 52, r: 18, t: 18, b: 36 };

const niceMax = (max: number) => {
    if (max <= 0) {
        return 1;
    }

    const pow = Math.pow(10, Math.floor(Math.log10(max)));
    const n = max / pow;
    const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;

    return nice * pow;
};

export const compactNumber = (value: number) => {
    const abs = Math.abs(value);

    if (abs >= 1_000_000) {
        return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    }

    if (abs >= 1000) {
        return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    }

    return `${Math.round(value * 100) / 100}`.replace(/\.00$/, '');
};

export const formatChartDate = (t: number, granularity: 'day' | 'week' | 'month') => {
    const date = new Date(t * 1000);
    const dd = `${date.getUTCDate()}`.padStart(2, '0');
    const mm = `${date.getUTCMonth() + 1}`.padStart(2, '0');
    const yyyy = date.getUTCFullYear();

    if (granularity === 'month') {
        return `${mm}.${yyyy}`;
    }

    return `${dd}.${mm}`;
};

const pointsOf = (series: ChartSeries[]) => series[0]?.points || [];

export const AdminLineChart = ({
    series,
    height = 280,
    formatX = (t) => formatChartDate(t, 'day'),
    formatY = compactNumber,
    emptyMessage,
}: LineChartProps) => {
    const [, , LangString] = LangBlock();
    const [hover, setHover] = React.useState<number | null>(null);
    const wrapRef = React.useRef<HTMLDivElement>(null);
    const points = pointsOf(series);
    const innerW = VIEW_W - PAD.l - PAD.r;
    const innerH = VIEW_H - PAD.t - PAD.b;
    const maxValue = niceMax(Math.max(0, ...series.flatMap((item) => item.points.map((p) => p.v))));

    if (!points.length || series.every((item) => item.points.every((p) => p.v === 0))) {
        return <div className="admin-empty">{emptyMessage ?? LangString('AdminChartNoData')}</div>;
    }

    const xAt = (index: number) => {
        if (points.length === 1) {
            return PAD.l + innerW / 2;
        }

        return PAD.l + (index / (points.length - 1)) * innerW;
    };

    const yAt = (value: number) => PAD.t + innerH - (value / maxValue) * innerH;

    const pathFor = (item: ChartSeries) =>
        item.points
            .map((point, index) => `${index === 0 ? 'M' : 'L'} ${xAt(index)} ${yAt(point.v)}`)
            .join(' ');

    const areaFor = (item: ChartSeries) => {
        const line = pathFor(item);
        const lastX = xAt(item.points.length - 1);
        const firstX = xAt(0);

        return `${line} L ${lastX} ${PAD.t + innerH} L ${firstX} ${PAD.t + innerH} Z`;
    };

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
        y: PAD.t + innerH * (1 - ratio),
        label: formatY(maxValue * ratio),
    }));

    const xTickEvery = Math.max(1, Math.ceil(points.length / 6));
    const xTicks = points
        .map((point, index) => ({ index, point }))
        .filter(({ index }) => index % xTickEvery === 0 || index === points.length - 1);

    const onMove = (event: React.MouseEvent<SVGSVGElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = (event.clientX - rect.left) / rect.width;
        const x = ratio * VIEW_W;
        let nearest = 0;
        let best = Infinity;

        points.forEach((_, index) => {
            const dx = Math.abs(xAt(index) - x);

            if (dx < best) {
                best = dx;
                nearest = index;
            }
        });

        setHover(nearest);
    };

    const hoverX = hover === null ? 0 : xAt(hover);
    const tooltipLeft =
        hover === null || !wrapRef.current
            ? 0
            : Math.min(
                  Math.max(8, (hoverX / VIEW_W) * wrapRef.current.clientWidth - 80),
                  wrapRef.current.clientWidth - 180,
              );

    return (
        <div className="admin-chart" ref={wrapRef} onMouseLeave={() => setHover(null)}>
            <svg
                className="admin-chart__svg"
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                style={{ height }}
                onMouseMove={onMove}
                role="img"
            >
                {yTicks.map((tick) => (
                    <g key={tick.y}>
                        <line
                            x1={PAD.l}
                            x2={VIEW_W - PAD.r}
                            y1={tick.y}
                            y2={tick.y}
                            className="admin-chart__grid"
                        />
                        <text x={PAD.l - 8} y={tick.y + 4} className="admin-chart__axis" textAnchor="end">
                            {tick.label}
                        </text>
                    </g>
                ))}
                {xTicks.map(({ index, point }) => (
                    <text
                        key={point.t}
                        x={xAt(index)}
                        y={VIEW_H - 10}
                        className="admin-chart__axis"
                        textAnchor="middle"
                    >
                        {formatX(point.t)}
                    </text>
                ))}
                {series.map((item) => (
                    <g key={item.key}>
                        <path d={areaFor(item)} fill={item.color} className="admin-chart__area" />
                        <path
                            d={pathFor(item)}
                            fill="none"
                            stroke={item.color}
                            className="admin-chart__line"
                        />
                    </g>
                ))}
                {hover !== null ? (
                    <>
                        <line
                            x1={hoverX}
                            x2={hoverX}
                            y1={PAD.t}
                            y2={PAD.t + innerH}
                            className="admin-chart__rule"
                        />
                        {series.map((item) => (
                            <circle
                                key={item.key}
                                cx={hoverX}
                                cy={yAt(item.points[hover]?.v || 0)}
                                r="4.5"
                                fill={item.color}
                                stroke="#0a0a0a"
                                strokeWidth="2"
                            />
                        ))}
                    </>
                ) : null}
            </svg>
            <div className="admin-chart__legend">
                {series.map((item) => (
                    <span key={item.key} className="admin-chart__legend-item">
                        <i style={{ background: item.color }} />
                        {item.label}
                    </span>
                ))}
            </div>
            {hover !== null && points[hover] ? (
                <div className="admin-chart__tooltip" style={{ left: tooltipLeft }}>
                    <strong>{formatX(points[hover].t)}</strong>
                    {series.map((item) => (
                        <div key={item.key}>
                            <i style={{ background: item.color }} />
                            {item.label}: {formatY(item.points[hover]?.v || 0)}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

type DonutItem = {
    key: string;
    label: string;
    value: number;
    color: string;
};

export const AdminDonutChart = ({
    items,
    formatValue = compactNumber,
    emptyMessage,
}: {
    items: DonutItem[];
    formatValue?: (value: number) => string;
    emptyMessage?: string;
}) => {
    const [, , LangString] = LangBlock();
    const [hover, setHover] = React.useState<string | null>(null);
    const total = items.reduce((sum, item) => sum + item.value, 0);

    if (total <= 0) {
        return <div className="admin-empty">{emptyMessage ?? LangString('AdminChartNoData')}</div>;
    }

    const r = 42;
    const c = 2 * Math.PI * r;
    let offset = 0;

    return (
        <div className="admin-donut">
            <svg viewBox="0 0 120 120" className="admin-donut__svg">
                {items.map((item) => {
                    const len = (item.value / total) * c;
                    const dash = `${len} ${c - len}`;
                    const current = offset;
                    offset += len;

                    return (
                        <circle
                            key={item.key}
                            cx="60"
                            cy="60"
                            r={r}
                            fill="none"
                            stroke={item.color}
                            strokeWidth={hover && hover !== item.key ? 10 : 14}
                            strokeDasharray={dash}
                            strokeDashoffset={-current}
                            transform="rotate(-90 60 60)"
                            className="admin-donut__slice"
                            onMouseEnter={() => setHover(item.key)}
                            onMouseLeave={() => setHover(null)}
                        />
                    );
                })}
                <text x="60" y="56" textAnchor="middle" className="admin-donut__total">
                    {formatValue(total)}
                </text>
                <text x="60" y="72" textAnchor="middle" className="admin-donut__total-label">
                    {LangString('AdminDonutTotal')}
                </text>
            </svg>
            <div className="admin-donut__legend">
                {items.map((item) => (
                    <button
                        key={item.key}
                        type="button"
                        className={`admin-donut__legend-item${hover === item.key ? ' is-active' : ''}`}
                        onMouseEnter={() => setHover(item.key)}
                        onMouseLeave={() => setHover(null)}
                    >
                        <i style={{ background: item.color }} />
                        <span>{item.label}</span>
                        <b>{formatValue(item.value)}</b>
                    </button>
                ))}
            </div>
        </div>
    );
};

type BarItem = {
    key: string;
    label: string;
    value: number;
    display?: string;
    color?: string;
};

export const AdminHBarList = ({
    items,
    emptyMessage,
}: {
    items: BarItem[];
    emptyMessage?: string;
}) => {
    const [, , LangString] = LangBlock();
    const max = Math.max(0, ...items.map((item) => item.value));

    if (!items.length || max <= 0) {
        return <div className="admin-empty">{emptyMessage ?? LangString('AdminChartNoData')}</div>;
    }

    return (
        <div className="admin-hbar">
            {items.map((item) => (
                <div key={item.key} className="admin-hbar__row">
                    <div className="admin-hbar__meta">
                        <span>{item.label}</span>
                        <b>{item.display ?? compactNumber(item.value)}</b>
                    </div>
                    <div className="admin-hbar__track">
                        <div
                            className="admin-hbar__fill"
                            style={{
                                width: `${Math.max(4, (item.value / max) * 100)}%`,
                                background: item.color || 'var(--admin-accent)',
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

export const AdminDelta = ({
    current,
    previous,
}: {
    current: number;
    previous: number | null | undefined;
}) => {
    const [, , LangString] = LangBlock();

    if (previous === null || previous === undefined) {
        return null;
    }

    if (previous === 0 && current === 0) {
        return <span className="admin-delta admin-delta--neutral">{LangString('AdminDeltaZero')}</span>;
    }

    if (previous === 0) {
        return <span className="admin-delta admin-delta--ok">{LangString('AdminDeltaNew')}</span>;
    }

    const pct = percentChangeLocal(current, previous);
    const up = pct >= 0;

    return (
        <span className={`admin-delta ${up ? 'admin-delta--ok' : 'admin-delta--danger'}`}>
            {up ? '▲' : '▼'} {Math.abs(pct)}% {LangString('AdminDeltaVsPrev')}
        </span>
    );
};

const percentChangeLocal = (current: number, previous: number) =>
    Math.round(((current - previous) / previous) * 1000) / 10;
